import React, { useState, useEffect, useMemo, useCallback } from 'react'; // 添加性能优化hooks
import {
  Toolbar,
  Typography,
  Button,
  Tabs,
  Tab,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginModal from './LoginModal';
import { getCurrentUser, signOut as userSignOut } from '../services/userReadingService';
import HistoryIcon from '@mui/icons-material/History';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate, useLocation } from 'react-router-dom';
import { AUTH_STATE_CHANGE_EVENT } from './UserReadings';
import { getCharacters } from '../data/characters';
import { languages, useLanguage, setLanguage, navigateWithLang, getLanguage, LANGUAGE_CHANGE_EVENT } from '../data/languages';
  
const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.text.primary,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: theme.palette.text.primary,
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  '&:hover': {
    color: theme.palette.text.primary,
    opacity: 0.7,
  },
}));

const NavContainer = styled('nav')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: '64px',
  backgroundColor: 'rgba(255, 255, 255, 0.4)',
  backdropFilter: 'blur(15px)',
  WebkitBackdropFilter: 'blur(15px)',
  zIndex: theme.zIndex.appBar,
  borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
}));

const NavBar = ({
  currentPage,
  activeNavLink,
  onNavigate,
  onNavLinkChange,
  onCharacterHover
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const currentLang = useLanguage(); // 响应式语言状态
  const config = useMemo(() => languages[currentLang], [currentLang]); // 优化语言配置获取

  // 监听URL变化，确保语言状态与URL保持同步
  useEffect(() => {
    const urlLang = getLanguage();
    // 强制同步URL中的语言参数到组件状态
    window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGE_EVENT, { detail: { language: urlLang } }));
  }, [location.search]); // 仅监听location.search变化，避免与currentLang的循环依赖

  // 监听路由变化，确保导航栏标签与当前路由保持同步
  useEffect(() => {
    const path = location.pathname;
    let newActiveTab = 'character'; // 默认标签

    if (path === '/readings') {
      newActiveTab = 'readings';
    } else if (path === '/fortune') {
      newActiveTab = 'fortune';
    } else if (path === '/character') {
      newActiveTab = 'character';
    }
    // "/" 和 "/about" 都映射到 'character' 标签

    if (newActiveTab !== activeNavLink) {
      onNavLinkChange(newActiveTab);
    }
  }, [location.pathname, activeNavLink, onNavLinkChange]);

  // Check for logged in user on component mount
  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      setCurrentUser(user);
      setIsLoggedIn(true);
    }
    
    // Listen for auth state changes
    const handleAuthStateChange = () => {
      const updatedUser = getCurrentUser();
      setCurrentUser(updatedUser);
      if (updatedUser) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
    };
    
    // Add event listener for auth state changes
    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);
    
    // Clean up event listener on unmount
    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);
    };
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    onNavLinkChange(newValue);
    
    if (newValue === 'readings') {
      navigateWithLang(navigate, '/readings');
    } else if (newValue === 'fortune') {
      // Select a random character from the characters array for fortune reading
      const allCharacters = getCharacters(currentLang);
      const randomIndex = Math.floor(Math.random() * allCharacters.length);
      const randomCharacter = allCharacters[randomIndex];
      
      // Navigate to the fortune page with the random character
      navigateWithLang(navigate, '/fortune', { 
        state: { 
          character: randomCharacter,
          isDailyFortune: true
        } 
      });
    } else if (newValue === 'character') {
      navigateWithLang(navigate, '/character');
    }
  };

  const handleLogoClick = () => {
    navigateWithLang(navigate, '/');
  };

  const handleLanguageChange = useCallback(() => { // 处理语言切换，优化性能
    const newLang = currentLang === 'en' ? 'zh' : 'en';
    setLanguage(newLang); // 更新URL并触发全局语言状态变更
  }, [currentLang]);

  const handleOpenLoginModal = () => {
    setLoginModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setLoginModalOpen(false);
  };
  
  const handleUserLogin = (user) => {
    setCurrentUser(user);
  };
  
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  const handleViewReadings = () => {
    handleUserMenuClose();
    navigateWithLang(navigate, '/readings');
  };
  
  const handleSignOut = () => {
    handleUserMenuClose();
    
    // Sign out from Google if needed
    if (window.gapi && window.gapi.auth2) {
      const auth2 = window.gapi.auth2.getAuthInstance();
      if (auth2) {
        auth2.signOut();
      }
    }
    
    // Use the signOut function from userReadingService
    userSignOut();
    setCurrentUser(null);
  };

  return (
    <>
      <NavContainer>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}> {/* Logo和语言切换区域 */}
            <Typography
              variant="h6"
              component="div"
              onClick={handleLogoClick}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: 1,
                '&:hover': { opacity: 0.7 }
              }}
            >
              {config.navbar.logo}
            </Typography>
            <Button onClick={handleLanguageChange} size="small" sx={{ // 圆角语言切换按钮
              borderRadius: '20px', px: 2, py: 0.5, fontSize: '0.8rem', minWidth: '50px',
              border: '1px solid rgba(0,0,0,0.1)', color: theme.palette.text.primary,
              backgroundColor: 'rgba(255,255,255,0.1)', textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}>
              {currentLang === 'en' ? 'EN' : '中文'}
            </Button>
          </Box>

          {!isMobile && (
            <Box sx={{ mx: 'auto' }}>
              <StyledTabs
                value={activeNavLink}
                onChange={handleChange}
                centered
              >
                <StyledTab 
                  label={config.navbar.tabs.character} 
                  value="character"
                />
                <StyledTab 
                  label={config.navbar.tabs.fortune} 
                  value="fortune" 
                />
                <StyledTab 
                  label={config.navbar.tabs.readings} 
                  value="readings" 
                />
              </StyledTabs>
            </Box>
          )}

          {currentUser ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                onClick={handleUserMenuOpen}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {currentUser.imageUrl ? (
                  <Avatar 
                    src={currentUser.imageUrl} 
                    alt={currentUser.name}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                    {currentUser.name.charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <Typography variant="body2">
                  {currentUser.name}
                </Typography>
              </Button>
              
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: { borderRadius: 2, minWidth: 200 }
                }}
              >
                <MenuItem onClick={() => {handleUserMenuClose(); navigateWithLang(navigate, '/profile');}}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={config.navbar.profile} />
                </MenuItem>
                <MenuItem onClick={handleViewReadings}>
                  <ListItemIcon>
                    <HistoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={config.navbar.myReadings} />
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  <ListItemIcon>
                    <ExitToAppIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={config.navbar.signOut} />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleOpenLoginModal}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              {config.navbar.login}
            </Button>
          )}
        </Toolbar>
      </NavContainer>
      
      <LoginModal 
        open={loginModalOpen} 
        onClose={handleCloseLoginModal}
        onUserLogin={handleUserLogin}
      />
    </>
  );
};

export default NavBar; 